import {
  type HomeworkAnswer,
  type HomeworkItem,
  OFFLINE_ACTIONS,
  type Submission,
  addDays,
  homeworkToDo,
  shortDate,
  todayIso,
  useHomeworkActions,
  weekStart,
  useQueuedActions,
  useStudentHomework,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpeakButton } from '../components/SpeakButton';
import {
  Block,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  SUBJECT_GRAD,
  SubjectArt,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import { CaretDownIcon } from '../design/icons';
import { AnswerFiles, AttachmentList } from './AnswerFiles';
import type { StudentContext } from './home/model';

/*
 * «Домашні завдання» of the student in «Форми 2» (28.10). His first look: «пустой, справа пустое место,
 * разделено на странные блоки — может, одним блоком». So one block: the subjects on top as chips to narrow
 * the list (each with how many are open), then every task by when it is due — late, today, tomorrow, this
 * week, later — and the done ones folded at the end. A task is a soft card in its subject's gradient with
 * the subject's picture big and quiet behind (as the lessons of the day); it opens to a white sheet to
 * read it, listen to it and answer.
 */

type State = 'open' | 'late' | 'queued' | 'submitted' | 'returned' | 'accepted' | 'reading';

/** A task's state as a soft mark: its colour and its words. */
const STATE: Record<
  Exclude<State, 'open'>,
  { tone: Grad; key: 'none' | 'queued' | 'submitted' | 'returned' | 'accepted' | 'notRequired' }
> = {
  late: { tone: 'coral', key: 'none' },
  queued: { tone: 'butter', key: 'queued' },
  submitted: { tone: 'lavender', key: 'submitted' },
  returned: { tone: 'apricot', key: 'returned' },
  accepted: { tone: 'mint', key: 'accepted' },
  reading: { tone: 'stone', key: 'notRequired' },
};

/** One task: a row that opens to read it, hear it and answer. */
function Task({
  hw,
  submission,
  schoolId,
  studentId,
}: {
  hw: HomeworkItem;
  submission: Submission | undefined;
  schoolId: string;
  studentId: string;
}) {
  const { t } = useTranslation();
  const actions = useHomeworkActions();
  const today = todayIso();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(submission?.body ?? '');
  const [attachments, setAttachments] = useState<string[]>(submission?.attachments ?? []);
  // made once per answer: a retry after a lost connection is recognised by the server
  const [clientId] = useState(() => crypto.randomUUID());
  const queued = useQueuedActions<HomeworkAnswer>(OFFLINE_ACTIONS.submitHomework).some(
    (a) => a.homeworkId === hw.id && a.studentId === studentId,
  );
  const state: State = queued
    ? 'queued'
    : !hw.requiresSubmission
      ? 'reading'
      : !submission
        ? hw.dueOn < today
          ? 'late'
          : 'open'
        : submission.status === 'accepted'
          ? 'accepted'
          : submission.status === 'returned'
            ? 'returned'
            : 'submitted';
  const due =
    hw.dueOn === today
      ? t('homework.dueToday')
      : hw.dueOn < today
        ? t('homework.overdue', { date: shortDate(hw.dueOn) })
        : t('student.homework.due', { date: shortDate(hw.dueOn) });
  const canAnswer = hw.requiresSubmission && submission?.status !== 'accepted';
  const mark = state === 'open' ? undefined : STATE[state];

  const look = subjectLook(hw.subject);
  const tone = SUBJECT_GRAD[look.tone];
  return (
    <article
      className="relative isolate overflow-hidden rounded-[20px]"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <SubjectArt
        icon={look.icon}
        tone={tone}
        size={92}
        ghost
        className="pointer-events-none absolute -top-3 right-14 -z-10"
      />
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-[64px] w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="min-w-0 flex-1">
          <span
            className={`block text-[16px] leading-[22px] font-medium ${open ? '' : 'line-clamp-2'}`}
            style={{ color: INK }}
          >
            {hw.title}
          </span>
          <span className="mt-0.5 block text-[13px]">
            {hw.subject}
            {' · '}
            <span style={{ color: state === 'late' ? '#D9471A' : INK_SOFT }}>{due}</span>
          </span>
        </span>
        {mark && (
          <span
            className="shrink-0 rounded-[7px] bg-white/80 px-2 py-1 text-[12px] leading-none font-medium"
            style={{ color: GRAD[mark.tone].ink }}
          >
            {t(`homework.status.${mark.key}`)}
          </span>
        )}
        <CaretDownIcon
          size={14}
          weight="bold"
          aria-hidden
          color={INK}
          className={`shrink-0 transition-transform duration-300 ease-spring ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="motion-rise mx-2 mb-2 flex flex-col gap-3 rounded-[16px] bg-white p-4">
          {hw.body && (
            <p className="text-[15px] leading-6" style={{ color: INK }}>
              {hw.body}
            </p>
          )}
          <div>
            <SpeakButton text={[hw.subject, hw.title, hw.body].filter(Boolean).join('. ')} />
          </div>
          {hw.criteria && (
            <div className="rounded-[16px] p-3.5" style={{ backgroundColor: PAPER }}>
              <p className="text-[12px] font-medium" style={{ color: INK_FAINT }}>
                {t('homework.criteria')}
              </p>
              <ul className="mt-1 flex flex-col gap-0.5 text-[15px]" style={{ color: INK }}>
                {hw.criteria
                  .split('\n')
                  .filter(Boolean)
                  .map((line) => (
                    <li key={line}>• {line}</li>
                  ))}
              </ul>
            </div>
          )}
          {submission?.feedback && (
            <div
              className="rounded-[16px] p-3.5"
              style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
            >
              <p className="text-[12px] font-medium">{t('student.homework.teacherSays')}</p>
              <p className="mt-0.5 text-[15px] leading-6" style={{ color: INK }}>
                {submission.feedback}
              </p>
            </div>
          )}
          {canAnswer ? (
            <form
              className="flex flex-col gap-3"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                actions.submit.mutate({
                  schoolId,
                  homeworkId: hw.id,
                  courseId: hw.courseId,
                  studentId,
                  // a photo alone is an answer too
                  body: body.trim() || t('homework.files.onlyFiles'),
                  clientId,
                  attachments,
                  ...(submission && { existingId: submission.id }),
                });
              }}
            >
              <textarea
                aria-label={t('homework.answer')}
                className="min-h-28 w-full resize-y rounded-[16px] bg-white p-3.5 text-[15px] leading-6 outline-none ring-1 ring-[#E4E0D7] ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]"
                style={{ color: INK }}
                placeholder={t('student.homework.answerPlaceholder')}
                maxLength={10000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <AnswerFiles
                schoolId={schoolId}
                homeworkId={hw.id}
                studentId={studentId}
                value={attachments}
                onChange={setAttachments}
              />
              {queued && (
                <p className="text-[13px]" style={{ color: GRAD.butter.ink }}>
                  {t('offline.queuedAnswer')}
                </p>
              )}
              <div>
                <button
                  type="submit"
                  disabled={
                    (!body.trim() && attachments.length === 0) || actions.submit.isPending || queued
                  }
                  className={`${pillClass('dark')} pr-4.5 disabled:opacity-35`}
                >
                  {queued
                    ? t('offline.waiting')
                    : actions.submit.isPending
                      ? t('homework.submitting')
                      : submission
                        ? t('homework.resubmit')
                        : t('homework.submit')}
                </button>
              </div>
            </form>
          ) : (
            submission && (
              <div className="flex flex-col gap-2">
                <p className="text-[15px] leading-6" style={{ color: INK_SOFT }}>
                  «{submission.body}»
                </p>
                <AttachmentList paths={submission.attachments} />
              </div>
            )
          )}
        </div>
      )}
    </article>
  );
}

/** The groups of the list by when a task is due. */
type Group = 'late' | 'today' | 'tomorrow' | 'week' | 'later';
const GROUPS: ReadonlyArray<Group> = ['late', 'today', 'tomorrow', 'week', 'later'];

export function ShapesHomework({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { course?: string };
  const today = todayIso();
  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(weekStart(today), 6);
  const studentId = ctx.child.memberId;
  const data = useStudentHomework(studentId, addDays(today, -14));
  const homework = data.data?.homework ?? [];
  const submissions = data.data?.submissions ?? [];
  const todo = homeworkToDo(homework, submissions, today, tomorrow);
  const [showDone, setShowDone] = useState(false);
  const submissionOf = (id: string) => submissions.find((s) => s.homeworkId === id);

  // what is still to do: to hand in and not handed (or returned), or to read and not past
  const toDo = new Set([...todo.overdue, ...todo.open]);
  const active = (h: HomeworkItem) => toDo.has(h) || (!h.requiresSubmission && h.dueOn >= today);
  const course = search.course;
  const inCourse = (h: HomeworkItem) => !course || h.courseId === course;
  const subjects = [...new Set(homework.map((h) => h.courseId))]
    .map((courseId) => {
      const all = homework.filter((h) => h.courseId === courseId);
      return {
        courseId,
        subject: (all[0] as HomeworkItem).subject,
        open: all.filter(active).length,
        late: all.filter((h) => todo.overdue.includes(h)).length,
      };
    })
    .sort((a, b) => b.late - a.late || b.open - a.open || a.subject.localeCompare(b.subject, 'uk'));
  const shown = homework.filter(inCourse);
  const groupOf = (h: HomeworkItem): Group =>
    h.dueOn < today
      ? 'late'
      : h.dueOn === today
        ? 'today'
        : h.dueOn === tomorrow
          ? 'tomorrow'
          : h.dueOn <= weekEnd
            ? 'week'
            : 'later';
  const open = shown.filter(active).sort((a, b) => a.dueOn.localeCompare(b.dueOn));
  const done = shown.filter((h) => !active(h)).sort((a, b) => b.dueOn.localeCompare(a.dueOn));
  const count = homework.filter(active).length;
  const choose = (courseId: string | undefined) => {
    setShowDone(false);
    void navigate({ to: '/homework', search: courseId ? { course: courseId } : {}, replace: true });
  };
  const chip = (on: boolean) =>
    `inline-flex h-10 shrink-0 items-center gap-2 rounded-full pr-3.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`;

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('nav.homework')}
        note={count > 0 ? t('student.homework.active', { count }) : undefined}
      />
      <Block>
        {data.isPending ? (
          <div
            className="h-[320px] animate-pulse rounded-[22px]"
            style={{ backgroundColor: PAPER }}
            aria-busy
          />
        ) : homework.length === 0 ? (
          <div className="flex flex-col items-center py-14 text-center">
            <TaskArt kind="done" tone="lime" size={96} />
            <p className="mt-3 font-display text-[26px] font-light" style={{ color: INK }}>
              {t('homework.noHomework')}
            </p>
          </div>
        ) : (
          <>
            {/* the subjects as chips: narrow the list to one */}
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                aria-pressed={!course}
                onClick={() => choose(undefined)}
                className={`${chip(!course)} pl-4`}
                style={{ backgroundColor: !course ? '#E9E5DC' : PAPER, color: INK }}
              >
                {t('studentHome.shapes2.allSubjects')}
                <span style={{ color: INK_FAINT }}>{count}</span>
              </button>
              {subjects.map((f) => {
                const l = subjectLook(f.subject);
                const on = f.courseId === course;
                return (
                  <button
                    key={f.courseId}
                    type="button"
                    aria-pressed={on}
                    onClick={() => choose(on ? undefined : f.courseId)}
                    className={`${chip(on)} pl-1.5`}
                    style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
                  >
                    <SubjectArt icon={l.icon} tone={SUBJECT_GRAD[l.tone]} size={28} />
                    {f.subject}
                    {f.open > 0 && (
                      <span style={{ color: f.late > 0 ? '#D9471A' : INK_FAINT }}>{f.open}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {open.length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <TaskArt kind="done" tone="lime" size={88} />
                <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
                  {t('studentHome.shapes2.allDone')}
                </p>
              </div>
            ) : (
              GROUPS.map((g) => {
                const items = open.filter((h) => groupOf(h) === g);
                if (items.length === 0) return null;
                return (
                  <section key={g} className="mt-6">
                    <h2
                      className="mb-2.5 flex items-baseline gap-2 text-[15px] font-medium"
                      style={{ color: g === 'late' ? '#D9471A' : INK }}
                    >
                      {t(`studentHome.shapes2.due.${g}`)}
                      <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
                        {items.length}
                      </span>
                    </h2>
                    <div className="grid grid-cols-1 items-start gap-2 xl:grid-cols-2">
                      {items.map((hw) => (
                        <Task
                          key={hw.id}
                          hw={hw}
                          submission={submissionOf(hw.id)}
                          schoolId={ctx.schoolId}
                          studentId={studentId}
                        />
                      ))}
                    </div>
                  </section>
                );
              })
            )}

            {done.length > 0 && (
              <section className="mt-6 border-t pt-4" style={{ borderColor: LINE }}>
                <button
                  type="button"
                  aria-expanded={showDone}
                  onClick={() => setShowDone(!showDone)}
                  className="flex items-center gap-2 text-[15px] font-medium"
                  style={{ color: INK }}
                >
                  {t('student.homework.done')}
                  <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
                    {done.length}
                  </span>
                  <CaretDownIcon
                    size={12}
                    weight="bold"
                    aria-hidden
                    className={`transition-transform duration-300 ${showDone ? 'rotate-180' : ''}`}
                  />
                </button>
                {showDone && (
                  <div className="mt-3 grid grid-cols-1 items-start gap-2 xl:grid-cols-2">
                    {done.map((hw) => (
                      <Task
                        key={hw.id}
                        hw={hw}
                        submission={submissionOf(hw.id)}
                        schoolId={ctx.schoolId}
                        studentId={studentId}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </Block>
    </div>
  );
}
